import posthog from "posthog-js";

/**
 * PostHog analytics — free→paid conversion funnel instrumentation.
 *
 * Guardrail 1 applies to analytics events exactly as it does to API
 * responses: event properties may carry only directional / tier / count
 * data — never weights, sub-scores, normalization parameters,
 * weights_version/config_version, or anything Property 8 strips from API
 * responses. The EventProps map below is the compile-time allowlist; add
 * new events there, not as inline posthog.capture() strings.
 *
 * When VITE_POSTHOG_KEY is unset (local dev), every function here is a
 * silent no-op — the app must never break for lack of an analytics key.
 */

type Tier = "free" | "paid";

/** Auth's tier claim says "paid"; funnel events use the product name "pro". */
export function tierLabel(tier: Tier | null | undefined): "free" | "pro" {
  return tier === "paid" ? "pro" : "free";
}

interface EventProps {
  /** Property/report submitted for scoring or free-tier lookup. */
  report_requested: { tier: "free" | "pro"; used_pro_trial: boolean };
  /**
   * A Tilt Map result rendered. `quadrant` is the user-visible verb
   * (Buy/Negotiate/Consider/Evaluate) — a directional label, Guardrail-1
   * safe. `locked` distinguishes the logged-out shared-report teaser.
   */
  report_viewed: {
    tier: "free" | "pro";
    context: "pro" | "trial" | "shared";
    quadrant?: string;
    locked?: boolean;
  };
  /** User ran into a free-tier limit. */
  paywall_hit: {
    reason: "pro_trial_exhausted" | "chat_turn_limit" | "pro_route";
  };
  /** Stripe checkout redirect initiated. */
  checkout_started: { tier: "free" | "pro" };
  /** AI follow-up sent. Context: verified score chat vs free property chat. */
  chat_message_sent: {
    tier: "free" | "pro";
    context: "verified_report" | "property";
  };
}

let enabled = false;

export function initAnalytics(): void {
  const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
  if (!key) return; // no key (local dev) → analytics is a silent no-op
  posthog.init(key, {
    api_host:
      (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ||
      "https://us.i.posthog.com",
    // The 2025-05-24 defaults enable SPA pageview capture on history
    // changes — required because react-router navigation never reloads
    // the page, so load-time-only pageviews would miss the whole funnel.
    defaults: "2025-05-24",
    // Session replay is toggled per-project in PostHog settings; masking
    // stays on so typed addresses/emails never appear in recordings.
    session_recording: { maskAllInputs: true },
  });
  enabled = true;
}

export function captureEvent<E extends keyof EventProps>(
  event: E,
  props: EventProps[E],
): void {
  if (!enabled) return;
  posthog.capture(event, props);
}

/** Join client events to the Cognito identity. Idempotent per session. */
export function identifyUser(sub: string, email: string, tier: Tier): void {
  if (!enabled || !sub) return;
  posthog.identify(sub, { email, tier: tierLabel(tier) });
}

/**
 * Sign-out only. Never call on the anonymous "no session" path — reset()
 * mints a fresh anonymous ID, which would shred pre-signup funnels.
 */
export function resetUser(): void {
  if (!enabled) return;
  posthog.reset();
}
